import { Button, Modal, Slider } from 'antd'
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { MdZoomIn, MdZoomOut } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import {
    MAX_ZOOM,
    MIN_ZOOM,
    clampOffset,
    cropRect,
    displaySize,
    initialCrop,
    zoomTo,
    type CropState,
} from '@/Ship/Helpers/photoCrop'
import { PHOTO_OUTPUT_SIZE } from '@/Ship/Helpers/userPhoto'

import styles from './UserPhoto.module.css'

/** Сторона кадру у вікні, px */
const VIEWPORT = 280
const KEY_STEP = 12
const ZOOM_STEP = 0.2

interface PhotoCropModalProps {
    /** Object URL вибраного файлу; null — вікно закрите */
    src: string | null
    busy: boolean
    onCancel: () => void
    onSave: (blob: Blob) => void
}

/** Рендерить обраний кадр у квадрат PHOTO_OUTPUT_SIZE (JPEG) */
const renderCrop = (image: HTMLImageElement, state: CropState): Promise<Blob | null> => {
    const { sx, sy, size } = cropRect(state, image.naturalWidth, image.naturalHeight, VIEWPORT)
    const output = Math.min(PHOTO_OUTPUT_SIZE, Math.round(size))
    const canvas = document.createElement('canvas')
    canvas.width = output
    canvas.height = output
    const context = canvas.getContext('2d')
    if (!context) return Promise.resolve(null)
    // Прозорий PNG у JPEG стає білим, а не чорним
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, output, output)
    context.imageSmoothingQuality = 'high'
    context.drawImage(image, sx, sy, size, size, 0, 0, output, output)
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
}

/**
 * Вікно обрізання фото профілю: круглий кадр, перетягування мишею/пальцем,
 * зум повзунком, колесом і клавішами (+/−, стрілки). Зберігає квадрат 512×512.
 */
export const PhotoCropModal = ({ src, busy, onCancel, onSave }: PhotoCropModalProps) => {
    const imageRef = useRef<HTMLImageElement | null>(null)
    const viewportRef = useRef<HTMLDivElement | null>(null)
    const dragRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null)
    const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
    const [crop, setCrop] = useState<CropState>({ zoom: MIN_ZOOM, x: 0, y: 0 })
    const [dragging, setDragging] = useState(false)

    // Нове зображення — скидаємо кадр до центру
    useEffect(() => {
        setNatural(null)
    }, [src])

    const onImageLoad = () => {
        const image = imageRef.current
        if (!image) return
        const size = { width: image.naturalWidth, height: image.naturalHeight }
        setNatural(size)
        setCrop(initialCrop(size.width, size.height, VIEWPORT))
    }

    const setZoom = (zoom: number, anchor?: { x: number; y: number }) => {
        if (!natural) return
        setCrop((state) => zoomTo(state, zoom, natural.width, natural.height, VIEWPORT, anchor))
    }

    const move = (dx: number, dy: number) => {
        if (!natural) return
        setCrop((state) =>
            clampOffset({ ...state, x: state.x + dx, y: state.y + dy }, natural.width, natural.height, VIEWPORT)
        )
    }

    // Колесо: зум навколо курсора. Нативний слухач, бо React вішає wheel як passive
    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport || !natural) return undefined
        const onWheel = (event: WheelEvent) => {
            event.preventDefault()
            const rect = viewport.getBoundingClientRect()
            const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top }
            setCrop((state) =>
                zoomTo(
                    state,
                    state.zoom * (event.deltaY < 0 ? 1.08 : 1 / 1.08),
                    natural.width,
                    natural.height,
                    VIEWPORT,
                    anchor
                )
            )
        }
        viewport.addEventListener('wheel', onWheel, { passive: false })
        return () => viewport.removeEventListener('wheel', onWheel)
    }, [natural])

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (!natural) return
        event.currentTarget.setPointerCapture(event.pointerId)
        dragRef.current = { x: event.clientX, y: event.clientY, startX: crop.x, startY: crop.y }
        setDragging(true)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current
        if (!drag || !natural) return
        setCrop((state) =>
            clampOffset(
                { ...state, x: drag.startX + event.clientX - drag.x, y: drag.startY + event.clientY - drag.y },
                natural.width,
                natural.height,
                VIEWPORT
            )
        )
    }

    const endDrag = () => {
        dragRef.current = null
        setDragging(false)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const actions: Record<string, () => void> = {
            ArrowLeft: () => move(KEY_STEP, 0),
            ArrowRight: () => move(-KEY_STEP, 0),
            ArrowUp: () => move(0, KEY_STEP),
            ArrowDown: () => move(0, -KEY_STEP),
            '+': () => setZoom(crop.zoom + ZOOM_STEP),
            '=': () => setZoom(crop.zoom + ZOOM_STEP),
            '-': () => setZoom(crop.zoom - ZOOM_STEP),
        }
        const action = actions[event.key]
        if (!action) return
        event.preventDefault()
        action()
    }

    const save = async () => {
        const image = imageRef.current
        if (!image || !natural) return
        const blob = await renderCrop(image, crop)
        if (blob) onSave(blob)
    }

    const size = natural ? displaySize(natural.width, natural.height, VIEWPORT, crop.zoom) : null

    return (
        <Modal
            open={src !== null}
            title="Фото профілю"
            width={380}
            centered
            destroyOnHidden
            maskClosable={!busy}
            closable={!busy}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={busy}>
                    Скасувати
                </Button>,
                <Button key="save" type="primary" loading={busy} disabled={!natural} onClick={() => void save()}>
                    Зберегти
                </Button>,
            ]}
        >
            <div className={cx(styles, 'cropBody')}>
                <div
                    ref={viewportRef}
                    className={`${cx(styles, 'cropViewport')} ${dragging ? cx(styles, 'cropDragging') : ''}`}
                    style={{ width: VIEWPORT, height: VIEWPORT }}
                    tabIndex={0}
                    role="application"
                    aria-label="Кадр фото. Перетягніть або використовуйте стрілки; плюс і мінус змінюють масштаб"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={onKeyDown}
                >
                    {src && (
                        <img
                            ref={imageRef}
                            src={src}
                            alt=""
                            draggable={false}
                            onLoad={onImageLoad}
                            className={cx(styles, 'cropImage')}
                            style={
                                size
                                    ? {
                                          width: size.width,
                                          height: size.height,
                                          transform: `translate(${crop.x}px, ${crop.y}px)`,
                                      }
                                    : { opacity: 0 }
                            }
                        />
                    )}
                    <div className={cx(styles, 'cropMask')} aria-hidden="true" />
                </div>

                <div className={cx(styles, 'cropZoom')}>
                    <Button
                        type="text"
                        size="small"
                        icon={<MdZoomOut />}
                        aria-label="Зменшити"
                        disabled={!natural || crop.zoom <= MIN_ZOOM}
                        onClick={() => setZoom(crop.zoom - ZOOM_STEP)}
                    />
                    <Slider
                        className={cx(styles, 'cropSlider')}
                        min={MIN_ZOOM}
                        max={MAX_ZOOM}
                        step={0.01}
                        value={crop.zoom}
                        disabled={!natural}
                        tooltip={{ open: false }}
                        onChange={(value) => setZoom(value)}
                        aria-label="Масштаб"
                    />
                    <Button
                        type="text"
                        size="small"
                        icon={<MdZoomIn />}
                        aria-label="Збільшити"
                        disabled={!natural || crop.zoom >= MAX_ZOOM}
                        onClick={() => setZoom(crop.zoom + ZOOM_STEP)}
                    />
                </div>
                <p className={cx(styles, 'cropHint')}>Перетягніть фото, щоб вибрати кадр</p>
            </div>
        </Modal>
    )
}
